import { useEffect, useRef, useState } from "react";
import { PhoneCall, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * PhoneAction — click pe numărul de telefon deschide un mini-dropdown
 * cu 2 opțiuni: „Apelează" și „Copiază".
 *
 *   • Apelare — folosește schema `tel:` care merge nativ pe:
 *       - iPhone / Android → deschide dialer-ul cu numărul precompletat
 *       - macOS (Safari / Chrome) → cu Continuity + iPhone lângă, se
 *         inițiază apelul direct din Mac prin iPhone (via FaceTime handler)
 *       - Windows / Linux desktop → cere aplicația default (Skype/Teams);
 *         dacă nu există handler nu se întâmplă nimic (așa e la orice `tel:`)
 *   • Copiază — `navigator.clipboard.writeText` + toast confirmare
 *
 * Props:
 *   phone (string) — required
 *   className (string) — optional wrapper class
 *   iconOnly (bool)  — dacă e true afișează doar iconița + număr (fără text)
 */
export default function PhoneAction({ phone, className = "", iconOnly = false, onCall = null }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const path = typeof e.composedPath === "function" ? e.composedPath() : [];
      if (wrapRef.current && path.includes(wrapRef.current)) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const cleanPhone = String(phone || "").replace(/\s+/g, "");
  const displayPhone = String(phone || "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cleanPhone);
      setCopied(true);
      toast.success("Copiat", { description: cleanPhone });
      setTimeout(() => setCopied(false), 1200);
    } catch (_e) {
      toast.error("Nu am putut copia");
    }
    setOpen(false);
  };

  const call = () => {
    window.location.href = `tel:${cleanPhone}`;
    setOpen(false);
    // Fire the callback asynchronously so it doesn't block the navigation.
    if (typeof onCall === "function") {
      try { onCall(cleanPhone); } catch (_e) { /* best-effort */ }
    }
  };

  if (!cleanPhone) return <span className="text-zinc-500">—</span>;

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center gap-1 font-mono text-sm text-sky-300 transition hover:text-sky-100"
        data-testid={`phone-action-${cleanPhone}`}
        title="Click pentru opțiuni"
      >
        <PhoneCall className="h-3 w-3" />
        {!iconOnly && <span>{displayPhone}</span>}
      </button>
      {open && (
        <div
          className="absolute left-0 top-full z-30 mt-1 min-w-[160px] overflow-hidden rounded-md border border-zinc-800 bg-zinc-950 shadow-xl"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={call}
            data-testid={`phone-action-call-${cleanPhone}`}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-zinc-100 transition hover:bg-emerald-500/15 hover:text-emerald-100"
          >
            <PhoneCall className="h-3.5 w-3.5 text-emerald-400" />
            <span>Apelează</span>
          </button>
          <button
            type="button"
            onClick={copy}
            data-testid={`phone-action-copy-${cleanPhone}`}
            className="flex w-full items-center gap-2 border-t border-zinc-900 px-3 py-2 text-left text-xs text-zinc-100 transition hover:bg-sky-500/15 hover:text-sky-100"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 text-sky-400" />}
            <span>{copied ? "Copiat" : "Copiază"}</span>
          </button>
        </div>
      )}
    </div>
  );
}
